import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  Target
} from 'lucide-react';

import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

import ScenarioResult from './ScenarioResult';

import { CDN_URL } from '@/config/cdn';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Sound effects URLs
const SOUND_URLS = {
  choice: `${CDN_URL}/audio/sfx/click.mp3`,
  success: `${CDN_URL}/audio/sfx/success-chime.mp3`,
  ending: `${CDN_URL}/audio/sfx/gong.mp3`,
};

// Play sound helper
const playSound = (soundKey) => {
  try {
    const audio = new Audio(SOUND_URLS[soundKey]);
    audio.volume = 0.3;
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore audio errors
  }
};

// ============================================
// Choice Button
// ============================================
const ChoiceButton = ({ choice, index, onSelect, disabled, showFeedback }) => {
  const [isSelected, setIsSelected] = useState(false);

  const handleClick = () => {
    if (disabled) return;
    setIsSelected(true);
    playSound('choice');
    setTimeout(() => {
      onSelect(choice);
    }, 300);
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
        isSelected
          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 scale-[0.98]'
          : disabled
            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 opacity-60'
            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 flex items-center justify-center font-semibold">
          {String.fromCharCode(65 + index)}
        </span>
        <div className="flex-1">
          <p className="font-medium text-gray-900 dark:text-gray-100">{choice.text}</p>
          {showFeedback && choice.feedback && (
            <p className="text-sm text-purple-600 mt-2 italic">{choice.feedback}</p>
          )}
        </div>
        <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isSelected ? 'translate-x-1' : ''}`} />
      </div>
    </button>
  );
};

// ============================================
// Node Display
// ============================================
const NodeDisplay = ({ node, onChoiceSelect, choicesDisabled, showFeedback }) => {
  if (!node) return null;

  return (
    <div className="space-y-6">
      {/* Scene Title */}
      {node.title && (
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{node.title}</h2>
      )}

      {/* Scene Image */}
      {node.image_url && (
        <div className="rounded-lg overflow-hidden">
          <img
            src={node.image_url}
            alt={node.title || 'Scene'}
            className="w-full h-48 object-cover"
          />
        </div>
      )}

      {/* Situation Text */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <p className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
          {node.situation_text}
        </p>
      </div>

      {/* Choices */}
      {node.choices && node.choices.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">What do you do?</p>
          {node.choices.map((choice, index) => (
            <ChoiceButton
              key={choice.id}
              choice={choice}
              index={index}
              onSelect={onChoiceSelect}
              disabled={choicesDisabled}
              showFeedback={showFeedback}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Main ScenarioPlayer Component
// ============================================
const ScenarioPlayer = ({ scenario, student, getAuthHeaders, onExit }) => {
  const [attemptId, setAttemptId] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [choicesMade, setChoicesMade] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastFeedback, setLastFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [result, setResult] = useState(null);
  const [startTime, setStartTime] = useState(null);

  // Start or resume scenario
  const startScenario = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${student.id}/scenarios/${scenario.id}/start`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAttemptId(data.attempt_id);
        setCurrentNode(data.node);
        setChoicesMade(data.choices_made || 0);
        setStartTime(Date.now());

        if (data.resumed) {
          // Show a brief message about resuming
          setLastFeedback('Continuing from where you left off...');
          setShowFeedback(true);
          setTimeout(() => {
            setShowFeedback(false);
            setLastFeedback(null);
          }, 2000);
        }
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to start scenario');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [scenario.id, student.id, getAuthHeaders]);

  useEffect(() => {
    startScenario();
  }, [startScenario]);

  // Submit a choice
  const handleChoiceSelect = async (choice) => {
    if (submitting || !attemptId) return;

    setSubmitting(true);
    setShowFeedback(false);

    const responseTime = startTime ? Date.now() - startTime : 0;

    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${student.id}/scenarios/attempts/${attemptId}/choice`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            choice_id: choice.id,
            response_time_ms: responseTime
          })
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Show feedback if any
        if (data.feedback) {
          setLastFeedback(data.feedback);
          setShowFeedback(true);
        }

        if (data.is_ending) {
          // Scenario complete
          playSound('ending');
          setResult({
            node: data.node,
            ending_type: data.ending_type,
            ending_message: data.ending_message,
            optimal_path: data.optimal_path,
            points_earned: data.points_earned
          });
        } else {
          // Continue to next node
          setChoicesMade(data.choices_made);
          setStartTime(Date.now());

          // Brief delay to show feedback before transitioning
          if (data.feedback) {
            setTimeout(() => {
              setCurrentNode(data.node);
              setShowFeedback(false);
              setLastFeedback(null);
            }, 1500);
          } else {
            setCurrentNode(data.node);
          }
        }
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to submit choice');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Abandon attempt
  const handleAbandon = async () => {
    if (!window.confirm('Are you sure you want to quit? Your progress will be lost.')) {
      return;
    }

    try {
      await fetch(
        `${API_BASE}/api/customer/students/${student.id}/scenarios/attempts/${attemptId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );
    } catch (e) {
      // Ignore errors
    }

    onExit(false);
  };

  // Handle play again from result screen
  const handlePlayAgain = () => {
    // Reset all state for fresh start
    setResult(null);
    setCurrentNode(null);
    setAttemptId(null);
    setChoicesMade(0);
    setSubmitting(false);
    setShowFeedback(false);
    setLastFeedback(null);
    setError(null);
    setLoading(true);
    startScenario();
  };

  // Show result screen
  if (result) {
    return (
      <ScenarioResult
        scenario={scenario}
        result={result}
        onPlayAgain={handlePlayAgain}
        onExit={() => onExit(true)}
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={handleAbandon}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Quit
        </Button>
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Target className="h-4 w-4" />
            {choicesMade} choices
          </span>
        </div>
      </div>

      {/* Scenario Title */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-3xl">{scenario.icon_emoji}</span>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{scenario.title}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
            {scenario.category?.replace('_', ' ')}
          </p>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30">
          <CardContent className="py-6 text-center">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700 dark:text-red-300">{error}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => { setError(null); startScenario(); }}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && !error && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Loading scenario...</p>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      {!loading && !error && currentNode && (
        <Card>
          <CardContent className="p-6">
            {/* Feedback overlay */}
            {showFeedback && lastFeedback && (
              <div className="mb-4 p-3 bg-purple-100 dark:bg-purple-900/50 rounded-lg text-purple-800 dark:text-purple-200 text-sm animate-fade-in">
                {lastFeedback}
              </div>
            )}

            <NodeDisplay
              node={currentNode}
              onChoiceSelect={handleChoiceSelect}
              choicesDisabled={submitting || showFeedback}
              showFeedback={false}
            />

            {/* Submitting indicator */}
            {submitting && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-purple-600 mr-2" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Processing...</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScenarioPlayer;
